/**
 * Connects a grid-placed trigger (`brand-expand-group`) to a remote brand-information panel by DOM id.
 */

class BrandExpandGroup extends HTMLElement {
  /** @type {AbortController} */
  #eventAbortController = new AbortController();

  connectedCallback() {
    const panelElementId = this.getAttribute('panel-element-id');
    if (!panelElementId) {
      return;
    }

    const panelElement = document.getElementById(panelElementId);
    if (!(panelElement instanceof HTMLElement)) {
      return;
    }

    const openButton = this.querySelector('[data-brand-expand-open]');
    const collapseButton = panelElement.querySelector('[data-brand-expand-collapse]');

    if (!(openButton instanceof HTMLElement)) {
      return;
    }

    if (!(collapseButton instanceof HTMLElement)) {
      return;
    }

    const eventSignal = this.#eventAbortController.signal;

    openButton.addEventListener(
      'click',
      () => {
        this.#expandRemotePanel(openButton, panelElement, collapseButton);
      },
      { signal: eventSignal }
    );

    collapseButton.addEventListener(
      'click',
      () => {
        this.#collapseRemotePanel(openButton, panelElement, collapseButton);
      },
      { signal: eventSignal }
    );
  }

  disconnectedCallback() {
    this.#eventAbortController.abort();
    this.#eventAbortController = new AbortController();
  }

  /**
   * @param {HTMLElement} openButton
   * @param {HTMLElement} panelElement
   * @param {HTMLElement} collapseButton
   */
  #expandRemotePanel(openButton, panelElement, collapseButton) {
    panelElement.dataset.brandPanelExpanded = 'true';
    openButton.setAttribute('aria-expanded', 'true');
    collapseButton.setAttribute('aria-expanded', 'true');
    this.classList.add('brand-expand-group--panel-is-expanded');
  }

  /**
   * @param {HTMLElement} openButton
   * @param {HTMLElement} panelElement
   * @param {HTMLElement} collapseButton
   */
  #collapseRemotePanel(openButton, panelElement, collapseButton) {
    panelElement.dataset.brandPanelExpanded = 'false';
    openButton.setAttribute('aria-expanded', 'false');
    collapseButton.setAttribute('aria-expanded', 'false');
    this.classList.remove('brand-expand-group--panel-is-expanded');

    this.#scrollTriggerSectionIntoViewAfterCollapse(panelElement);
  }

  /**
   * After the expandable panel finishes collapsing, restore scroll so the paired trigger section is aligned.
   * Layout height changes otherwise leave the viewport offset from the collapsed content.
   *
   * @param {HTMLElement} panelElement
   */
  #scrollTriggerSectionIntoViewAfterCollapse(panelElement) {
    const scrollTarget = this.closest('.shopify-section') ?? this;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';

    let settled = false;
    let fallbackTimerId = 0;

    const scrollOnce = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(fallbackTimerId);
      panelElement.removeEventListener('transitionend', onTransitionEnd);
      scrollTarget.scrollIntoView({ block: 'start', behavior, inline: 'nearest' });
    };

    /** @param {TransitionEvent} event */
    const onTransitionEnd = (event) => {
      if (event.target !== panelElement) return;
      if (event.propertyName !== 'block-size') return;
      scrollOnce();
    };

    panelElement.addEventListener('transitionend', onTransitionEnd);
    fallbackTimerId = window.setTimeout(scrollOnce, 280);
  }
}

if (!customElements.get('brand-expand-group')) {
  customElements.define('brand-expand-group', BrandExpandGroup);
}
